import { useEffect, type ReactNode } from "react"
import { Portal } from "./portal"

/** Right-side slide-in panel with a dimmed backdrop. */
export function Drawer({
  open,
  onClose,
  children,
  variant = "dark",
}: {
  open: boolean
  onClose: () => void
  children: ReactNode
  variant?: "dark" | "light"
}) {
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKeyDown)
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKeyDown)
      document.body.style.overflow = previous
    }
  }, [open, onClose])

  return (
    <Portal>
      <div className={`fixed inset-0 z-50 ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
        <div
          onClick={onClose}
          className={`absolute inset-0 bg-forest-950/50 backdrop-blur-sm transition-opacity duration-200 ${
            open ? "opacity-100" : "opacity-0"
          }`}
        />
        <div
          role="dialog"
          aria-modal="true"
          className={`absolute inset-y-0 right-0 flex w-full max-w-2xl flex-col transition-transform duration-300 ease-out ${
            variant === "light"
              ? "border-l border-gold-500 bg-white text-ink shadow-xl"
              : "rounded-none border-l border-forest-800 bg-forest-950 text-cream-50 shadow-lg shadow-black/20 sm:rounded-l-2xl"
          } ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {children}
        </div>
      </div>
    </Portal>
  )
}
